import { useState } from "react";
import { Rocket, Loader2 } from "lucide-react";
import { GoogleButton } from "@/components/GoogleButton";
import { registerEmail, loginEmail } from "@/lib/api";
import { maybeClaimReferral } from "@/lib/referral";

export default function Login() {
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const me = mode === "register" ? await registerEmail(email.trim(), password, name.trim()) : await loginEmail(email.trim(), password);
      await maybeClaimReferral(me?.is_new_user);
      // Same-origin login → token persists in THIS context (works inside the installed PWA,
      // no external OAuth redirect that breaks out of standalone mode).
      window.location.assign("/app");
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-gray-200 px-4 h-11 text-sm text-gray-900 outline-none transition-colors focus:border-brand-orange";

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col items-center justify-center px-6 py-10" data-testid="login-page">
      <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-8">
          <img src="/slip-logo.png" alt="Snaggedify" className="h-9 w-9 rounded-lg" />
          <span className="text-3xl font-bold tracking-tighter">Snaggedify <span className="text-gray-400 font-semibold">by Cheapify</span></span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">{mode === "register" ? "Create your account" : "Sign in"}</h1>
        <p className="mt-2 text-sm text-gray-500">
          Verify reseller deals, compare real prices and shop smarter.
        </p>

        {/* Early Access notice */}
        <div
          data-testid="login-early-access"
          className="mt-5 flex items-center gap-2 rounded-xl border border-brand-yellow/40 bg-brand-yellow/10 px-4 py-3 text-sm font-bold text-amber-700"
        >
          <Rocket className="h-4 w-4 shrink-0" /> Snaggedify v1.0 — Early Access (Alpha). Help shape it.
        </div>

        <div className="mt-6">
          <GoogleButton testId="login-google-btn" className="w-full" />
        </div>

        <div className="my-5 flex w-full items-center gap-3">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-400">or use email</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        {/* Email/password — works inside the installed PWA (no external redirect) */}
        <form onSubmit={submit} className="w-full space-y-3" data-testid="login-email-form">
          {mode === "register" && (
            <input className={inputCls} type="text" placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="login-name-input" />
          )}
          <input className={inputCls} type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
          <input className={inputCls} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="login-password-input" />
          {error && <p className="text-xs font-semibold text-red-600" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="login-submit-btn" className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-yellow to-brand-orange py-3 text-sm font-bold uppercase tracking-[0.08em] text-black transition-transform hover:scale-[1.02] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {mode === "register" ? "Create Account" : "Sign In"}
          </button>
        </form>

        <button
          type="button"
          onClick={() => { setError(""); setMode(mode === "register" ? "login" : "register"); }}
          data-testid="login-toggle-mode"
          className="mt-4 w-full text-center text-xs font-semibold text-gray-500 transition-colors hover:text-brand-orange"
        >
          {mode === "register" ? "Already have an account? Sign in" : "New here? Create an account"}
        </button>
      </div>
    </div>
  );
}
